import { useEffect } from 'react';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';

import { useGetPaymentSystems } from '@/modules/payment-systems';
import { useGetShops } from '@/modules/shops';
import { useShowResponseStatus } from '@/shared';

import {
  useGetConfigTelegramChatRoutingQuery,
  useUpdateConfigTelegramChatRoutingMutation,
} from '../store/config-api';
import { handleConfigTelegramChatRoutingUpdateError } from '../utils/helpers/config-errors';

import type {
  TConfigTelegramChatRoute,
  TConfigTelegramChatRouting,
  TConfigTelegramClientChat,
  TConfigTelegramRelayChat,
} from '../store/types';

type FormData = TConfigTelegramChatRouting;

const createRoute = (): TConfigTelegramChatRoute => ({
  paymentSystem: '',
  chatId: '',
});

const createClientChat = (): TConfigTelegramClientChat => ({
  name: '',
  chatId: '',
  shopIds: [],
  routes: [createRoute()],
  lookupFields: ['payment_id', 'remote_id'],
  missingReferenceMessage: 'Мне не хватает ID или Remote ID, вижу только чек',
  unresolvedMessage: 'Я не справляюсь, зову оператора',
  successMessage: 'успех\nПосле проверки оператором - поступят на баланс',
});

const createRelayChat = (): TConfigTelegramRelayChat => ({
  name: '',
  chatId: '',
  destinationChatId: '',
  confirmationReaction: '✅',
  confirmationAlternateReaction: '💯',
  confirmationThirdReaction: '👌',
  confirmationCustomEmojiId: '',
  reminderUsernames: '',
  confirmationText: 'есть',
  successMessage: 'Есть',
});

const validateChatId = (value: string) => {
  const chatId = Number(value);

  return /^-?\d+$/.test(value) && Number.isSafeInteger(chatId) && chatId !== 0
    ? null
    : 'Введите корректный ID чата';
};

export const useConfigTelegramChatRouting = () => {
  const {
    data,
    isLoading: isGetLoading,
    isSuccess: isGetSuccess,
    isError: isGetError,
  } = useGetConfigTelegramChatRoutingQuery();
  const [updateTelegramChatRouting, {
    isLoading: isUpdateLoading,
    isSuccess: isUpdateSuccess,
    isError: isUpdateError,
    error: updateError,
  }] = useUpdateConfigTelegramChatRoutingMutation();
  const { data: shops, isLoading: isShopsLoading } = useGetShops();
  const { data: paymentSystems, isLoading: isPaymentSystemsLoading } = useGetPaymentSystems();
  const error = handleConfigTelegramChatRoutingUpdateError(updateError);

  useShowResponseStatus({
    isSuccess: isUpdateSuccess,
    successMessage: 'Маршрутизация чеков успешно сохранена',
    isError: isGetError || (isUpdateError && !error),
    error,
  });

  const form = useForm<FormData>({
    mode: 'uncontrolled',
    initialValues: {
      enabled: false,
      operators: '',
      clientChats: [],
      relayChats: [],
    },
    validate: {
      clientChats: {
        name: (value) => value.trim() ? null : 'Введите название чата',
        chatId: validateChatId,
        lookupFields: (value) => value.length ? null : 'Выберите хотя бы одно поле',
        routes: {
          paymentSystem: (value) => value ? null : 'Выберите платёжную систему',
          chatId: validateChatId,
        },
      },
      relayChats: {
        name: (value) => value.trim() ? null : 'Введите название чата',
        chatId: validateChatId,
        destinationChatId: validateChatId,
        reminderUsernames: (value) => value.split(/[\s,]+/).filter(Boolean)
          .every((username) => /^@?[A-Za-z0-9_]{1,32}$/.test(username))
          && value.split(/[\s,]+/).filter(Boolean).length <= 100
          ? null : 'Укажите до 100 корректных Telegram-логинов',
        confirmationCustomEmojiId: (value) => !value.trim() || /^[0-9]+$/.test(value.trim())
          ? null : 'Введите числовой ID эмодзи',
        confirmationText: (value, values, path) => (
          value.trim() || values.relayChats[Number(path.split('.')[1])].confirmationReaction
          || values.relayChats[Number(path.split('.')[1])].confirmationAlternateReaction
          || values.relayChats[Number(path.split('.')[1])].confirmationThirdReaction
          || values.relayChats[Number(path.split('.')[1])].confirmationCustomEmojiId.trim()
            ? null
            : 'Укажите реакцию или слово подтверждения'
        ),
        successMessage: (value) => value.trim() ? null : 'Введите текст ответа клиенту',
      },
    },
  });
  const setFormValues = form.setValues;

  useEffect(() => {
    if (isGetSuccess && data) {
      setFormValues(structuredClone(data));
    }
  }, [data, isGetSuccess, setFormValues]);

  const configuredShopIds = data?.clientChats.flatMap((clientChat) => clientChat.shopIds) || [];
  const shopsById = new Map((shops || []).map((shop) => [String(shop.id), shop]));
  const shopOptions = [...new Set([
    ...shopsById.keys(),
    ...configuredShopIds,
  ])]
    .map((shopId) => ({
      value: shopId,
      label: shopsById.has(shopId)
        ? `${shopsById.get(shopId)?.name} (#${shopId})`
        : `#${shopId} (недоступен)`,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));

  const configuredPaymentSystems = (
    data?.clientChats.flatMap((clientChat) => (
      clientChat.routes.map((route) => route.paymentSystem)
    )) || []
  );
  const paymentSystemOptions = [...new Set([
    ...(paymentSystems || []).map((paymentSystem) => paymentSystem.name),
    ...configuredPaymentSystems,
  ])]
    .sort()
    .map((paymentSystem) => ({
      value: paymentSystem,
      label: paymentSystem,
    }));

  const handleTelegramChatRoutingUpdate = async (params: TConfigTelegramChatRouting) => {
    await updateTelegramChatRouting(params);
  };

  const handleSubmit = (values: FormData) => {
    if (values.enabled && values.clientChats.length === 0 && values.relayChats.length === 0) {
      form.setFieldError('clientChats', 'Добавьте клиентский чат или чат без заявок');

      return;
    }

    const sourceIds = [
      ...values.clientChats.map((chat) => Number(chat.chatId)),
      ...values.relayChats.map((chat) => Number(chat.chatId)),
    ];
    const destinationIds = new Set([
      ...values.clientChats.flatMap((chat) => chat.routes.map((route) => Number(route.chatId))),
      ...values.relayChats.map((chat) => Number(chat.destinationChatId)),
    ]);

    if (new Set(sourceIds).size !== sourceIds.length) {
      form.setFieldError('clientChats', 'ID исходных чатов не должны повторяться');

      return;
    }
    if (sourceIds.some((chatId) => destinationIds.has(chatId))) {
      form.setFieldError('clientChats', 'Исходный чат не может быть чатом проверки');

      return;
    }

    modals.openConfirmModal({
      title: 'Сохранение маршрутизации чеков',
      centered: true,
      children: 'Вы точно хотите сохранить маршрутизацию чеков?',
      labels: { confirm: 'Подтвердить', cancel: 'Отмена' },
      confirmProps: { color: 'blue' },
      onConfirm: () => handleTelegramChatRoutingUpdate(values),
    });
  };

  const addClientChat = () => {
    form.insertListItem('clientChats', createClientChat());
  };

  const removeClientChat = (clientChatIndex: number) => {
    form.removeListItem('clientChats', clientChatIndex);
  };

  const addRoute = (clientChatIndex: number) => {
    form.insertListItem(`clientChats.${clientChatIndex}.routes`, createRoute());
  };

  const removeRoute = (clientChatIndex: number, routeIndex: number) => {
    form.removeListItem(`clientChats.${clientChatIndex}.routes`, routeIndex);
  };

  const addRelayChat = () => {
    form.insertListItem('relayChats', createRelayChat());
  };

  const removeRelayChat = (index: number) => {
    form.removeListItem('relayChats', index);
  };

  return {
    form,
    isLoading: (
      isGetLoading
      || isUpdateLoading
      || isShopsLoading
      || isPaymentSystemsLoading
    ),
    shopOptions,
    paymentSystemOptions,
    addClientChat,
    removeClientChat,
    addRoute,
    removeRoute,
    addRelayChat,
    removeRelayChat,
    handleSubmit,
  };
};
